/**
 * bomb.ts -- Phase 51: Bomb enemy module.
 *
 * Slow cursor-tracking enemy with a fuse countdown arc. Explodes into
 * letter-glyph particles on detonation; blast damages player if in radius.
 * Module-level state for blast flash. Pure exported functions, no React.
 */

import type { Enemy, GameState, Particle, WordPoolEntry } from './types';
import { getSpawnPosition, ENEMY_FONT, PILL_PAD_X, measurePill, getCharXOffset } from './enemies';
import { activateWord } from './wordPool';
import { spawnParticle } from './particles';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BOMB_SPEED = 0.8;               // CSS px/frame @ 60fps (~50 px/s) — lazy follow
export const BOMB_FUSE = 240;         // frames (4s at 60fps)
const BOMB_BASE_RADIUS = 80;          // px at full word
const BOMB_MIN_RADIUS = 30;           // px minimum blast radius
const BLAST_FLASH_DURATION = 8;       // frames
const PILL_BG = 'rgba(20, 20, 20, 0.9)';
const BOMB_BORDER = 'rgba(239, 68, 68, 0.9)';
const FONT_SIZE = 14;                 // match enemies.ts
const PILL_PAD_Y = 6;                 // match enemies.ts
const PILL_RADIUS = 8;                // match enemies.ts
const HIGHLIGHT = '#c084fc';          // typed portion color (same as homing)

// ---------------------------------------------------------------------------
// Module-level state (reset on game restart via resetBomb)
// ---------------------------------------------------------------------------

let blastFlash: { x: number; y: number; radius: number; framesLeft: number } | null = null;

// ---------------------------------------------------------------------------
// Helper: fuseColor
// ---------------------------------------------------------------------------

/**
 * Return the countdown arc color based on fuse elapsed progress.
 * progress: 0.0 = full fuse, 1.0 = detonating
 */
function fuseColor(progress: number): string {
  if (progress < 0.5) return 'rgba(34, 197, 94, 0.9)';   // green
  if (progress < 0.75) return 'rgba(251, 191, 36, 0.9)'; // amber
  return 'rgba(239, 68, 68, 0.9)';                        // red
}

// ---------------------------------------------------------------------------
// spawnBomb
// ---------------------------------------------------------------------------

/**
 * Pick a word from state.wordPool and create a bomb Enemy at a random edge position.
 * Sets type: 'bomb' and fuseTimer: BOMB_FUSE. If wordPool is empty or activateWord
 * returns null (non-fallback category), returns entry to pool and returns early.
 */
export function spawnBomb(state: GameState, cssW: number, cssH: number): void {
  if (state.wordPool.length === 0) return;

  const entry = state.wordPool.shift() as WordPoolEntry;

  const rect = activateWord(entry);
  if (rect === null && entry.category !== 'fallback') {
    state.wordPool.unshift(entry);
    return;
  }

  const spawn = getSpawnPosition(cssW, cssH);

  const enemy: Enemy = {
    word: entry.word,
    typed: 0,
    x: spawn.x,
    y: spawn.y,
    vx: 0,
    vy: 0,
    wobblePhase: 0,
    spawnCategory: entry.category,
    fontSize: entry.fontSize,
    type: 'bomb',
    fuseTimer: BOMB_FUSE,
  };

  state.enemies.push(enemy);
}

// ---------------------------------------------------------------------------
// updateBomb
// ---------------------------------------------------------------------------

/**
 * Advance a bomb enemy one frame: lazy-follow movement toward cursor + fuse decrement.
 * Called only when clearAllPhase === 'idle' (enforced by GameCanvas caller), so both
 * movement and fuseTimer automatically freeze during clear-all sequences.
 */
export function updateBomb(enemy: Enemy, mouseX: number, mouseY: number, dt: number): void {
  const dx = mouseX - enemy.x;
  const dy = mouseY - enemy.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist >= 1) {
    const nx = dx / dist;
    const ny = dy / dist;
    const step = BOMB_SPEED * dt * 60;
    enemy.x += nx * step;
    enemy.y += ny * step;
  }

  enemy.fuseTimer = (enemy.fuseTimer ?? BOMB_FUSE) - dt * 60;
}

// ---------------------------------------------------------------------------
// getBombBlastRadius
// ---------------------------------------------------------------------------

/**
 * Return current blast radius based on remaining untyped letters.
 * Scales proportionally from BOMB_BASE_RADIUS at full word to BOMB_MIN_RADIUS at fully typed.
 */
export function getBombBlastRadius(enemy: Enemy): number {
  const remaining = enemy.word.length - enemy.typed;
  return Math.max(BOMB_MIN_RADIUS, BOMB_BASE_RADIUS * (remaining / enemy.word.length));
}

// ---------------------------------------------------------------------------
// triggerBombDetonation
// ---------------------------------------------------------------------------

/**
 * Spawn radial letter-glyph particles for each remaining untyped letter and
 * set the module-level blastFlash state. Called by GameCanvas at the moment
 * fuseTimer hits 0.
 */
export function triggerBombDetonation(enemy: Enemy, particles: Particle[]): void {
  const remainingWord = enemy.word.slice(enemy.typed);
  const count = remainingWord.length;

  for (let i = 0; i < count; i++) {
    const angle = (i / Math.max(count, 1)) * 2 * Math.PI;
    const speed = 3 + Math.random() * 3;
    spawnParticle(
      remainingWord[i],
      enemy.x,
      enemy.y,
      particles,
      Math.cos(angle) * speed,
      Math.sin(angle) * speed,
      enemy.fontSize
    );
  }

  blastFlash = {
    x: enemy.x,
    y: enemy.y,
    radius: getBombBlastRadius(enemy),
    framesLeft: BLAST_FLASH_DURATION,
  };
}

// ---------------------------------------------------------------------------
// tickBlastFlash
// ---------------------------------------------------------------------------

/**
 * Decrement blast flash timer. Clears blastFlash when framesLeft reaches 0.
 */
export function tickBlastFlash(dt: number): void {
  if (blastFlash === null) return;
  blastFlash.framesLeft -= dt * 60;
  if (blastFlash.framesLeft <= 0) {
    blastFlash = null;
  }
}

// ---------------------------------------------------------------------------
// drawBombEnemy
// ---------------------------------------------------------------------------

/**
 * Render a bomb enemy pill with:
 * - Dark background + red border (BOMB_BORDER)
 * - Typed portion in HIGHLIGHT purple, untyped in white
 * - Clockwise countdown arc from 12 o'clock (green -> amber -> red as fuse drains)
 * - Opacity pulse when fuse is < 25% remaining (progress >= 0.75)
 */
export function drawBombEnemy(ctx: CanvasRenderingContext2D, enemy: Enemy): void {
  ctx.save();
  ctx.font = ENEMY_FONT;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  const { pillW, pillH } = measurePill(ctx, enemy.word, 0);
  const pillX = enemy.x - pillW / 2;
  const pillY = enemy.y - pillH / 2;
  const cx = enemy.x;
  const cy = enemy.y;

  // Fuse progress: 0 = full fuse, 1 = detonating
  const fuse = enemy.fuseTimer ?? BOMB_FUSE;
  const progress = 1 - fuse / BOMB_FUSE;

  // Pulse effect when fuse < 25% remaining (progress >= 0.75)
  if (progress >= 0.75) {
    enemy.wobblePhase += 0.15;
    ctx.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(enemy.wobblePhase));
  }

  // Pill background
  ctx.fillStyle = PILL_BG;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(pillX, pillY, pillW, pillH, PILL_RADIUS);
  } else {
    ctx.rect(pillX, pillY, pillW, pillH);
  }
  ctx.fill();

  // Red border (always shown for bomb enemies)
  ctx.strokeStyle = BOMB_BORDER;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Restore globalAlpha for text rendering
  ctx.globalAlpha = 1;

  // Render all characters: typed in HIGHLIGHT, untyped in white
  const textY = pillY + PILL_PAD_Y;

  for (let i = 0; i < enemy.word.length; i++) {
    const char = enemy.word[i];
    const charX = pillX + PILL_PAD_X + getCharXOffset(ctx, enemy.word, i);

    if (i < enemy.typed) {
      ctx.fillStyle = HIGHLIGHT;
    } else {
      ctx.fillStyle = '#ffffff';
    }

    ctx.fillText(char, charX, textY);
  }

  // Countdown arc: clockwise from 12 o'clock, shows remaining fuse
  const arcRadius = pillW / 2 + 6;
  const endAngle = -Math.PI / 2 + (1 - progress) * 2 * Math.PI;

  ctx.beginPath();
  ctx.arc(cx, cy, arcRadius, -Math.PI / 2, endAngle);
  ctx.strokeStyle = fuseColor(progress);
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.restore();
}

// ---------------------------------------------------------------------------
// drawBlastFlash
// ---------------------------------------------------------------------------

/**
 * Draw the semi-transparent red blast flash circle if active.
 * Called each render frame; no-op when blastFlash is null.
 */
export function drawBlastFlash(ctx: CanvasRenderingContext2D): void {
  if (blastFlash === null) return;

  ctx.save();
  ctx.fillStyle = 'rgba(239, 68, 68, 0.3)';
  ctx.beginPath();
  ctx.arc(blastFlash.x, blastFlash.y, blastFlash.radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// resetBomb
// ---------------------------------------------------------------------------

/**
 * Clear module-level blast flash state on game restart.
 * Bomb enemies in state.enemies are cleared by resetGameInPlace's state.enemies.length = 0.
 */
export function resetBomb(): void {
  blastFlash = null;
}
